import { Button } from '@mesa-labs/mesa-ui';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ErrorText } from './styles';
import { isEntrySubmitKey } from '../../../../utils/vendorActionItems';

// Entry form primitives: a full-width input, with any select and the submit button right-justified below it.
export const Form = styled.div`
  background: #F9FAFB;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
  padding: 8px;
`;

export const Row = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

// Under the input: the select and submit button, right-justified.
export const Controls = styled(Row)`
  justify-content: flex-end;
`;

export const Input = styled.input`
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 4px;
  flex: 1;
  font-size: 14px;
  min-width: 0;
  padding: 7px 10px;

  &:focus { border-color: #7B5EA7; outline: none; }
`;

// One line that grows with each new line up to about eight, then scrolls.
const TextArea = styled(Input).attrs({ as: 'textarea', rows: 1 })`
  font-family: inherit;
  line-height: 20px;
  max-height: 176px;
  overflow-y: auto;
  resize: none;
`;

// The button stays on the input's last line as it grows.
const EntryRow = styled(Row)`
  align-items: flex-end;
`;

export const Select = styled.select`
  background: #fff;
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 4px;
  font-size: 12px;
  max-width: 180px;
  padding: 3px 6px;

  &:focus { border-color: #7B5EA7; outline: none; }
`;

type Props = {
  placeholder: string;
  submitText: string;
  // On for forms opened by "+ New"; off for one that is always shown.
  autoFocus?: boolean;
  // Shown beside the submit button; with one, both sit below the input instead of to its right.
  select?: React.ReactNode;
  // A failure from the last attempt, reported by the caller's mutation.
  error?: string;
  // Resolves to whether the entry was saved; the text is kept on failure so it isn't lost.
  onSubmit: (text: string) => Promise<boolean>;
};

// The drawer's Context sections' text entry (comments, Vendor Notes); the Action Item create form shares its layout.
export default function InlineEntryForm({
  placeholder, submitText, autoFocus = true, select, error, onSubmit,
}: Props): React.ReactElement {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const textArea = useRef<HTMLTextAreaElement>(null);

  // Fit the height to the text; reset first so it shrinks when lines are removed.
  useLayoutEffect(() => {
    const el = textArea.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.offsetHeight - el.clientHeight + el.scrollHeight}px`;
  }, [text]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const saved = await onSubmit(trimmed);
    setSaving(false);
    if (saved) setText('');
  };

  // Enter posts; Shift+Enter adds a line (see isEntrySubmitKey).
  const input = (
    <TextArea
      ref={textArea}
      autoFocus={autoFocus}
      placeholder={placeholder}
      value={text}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
      onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isEntrySubmitKey({
          key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing, keyCode: e.nativeEvent.keyCode,
        })) return;
        e.preventDefault();
        submit();
      }}
    />
  );
  const button = <Button text={submitText} fontSize={12} height={26} disabled={!text.trim()} isLoading={saving} onClick={submit} />;

  return (
    <Form>
      {select ? (
        <>
          {input}
          <Controls>
            {select}
            {button}
          </Controls>
        </>
      ) : (
        <EntryRow>
          {input}
          {button}
        </EntryRow>
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Form>
  );
}
